import React, { useState, useEffect } from "react";
import { Modal, Tabs, Tab, Badge, Table } from "react-bootstrap";
import moment from "moment";
import { DISPLAY_DATE_FORMAT, DB_HOURS_FORMAT } from "../../../constants";
import PacmanWrapper from "../../../components/dashboard/PacmanWrapper";
import { toPesoAmount } from "../../../helpers";
import DataTable from "react-data-table-component";

import { get } from "lodash";

const GuestInformationModal = ({
  show,
  onHide,
  guestId,
  api,
  onReservationClick,
}) => {
  const [guestData, setGuestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    if (show && guestId) {
      const fetchGuestDetails = async () => {
        setLoading(true);
        try {
          const { data } = await api.get(`/customer/${guestId}/details`);
          setGuestData(data);
        } catch (error) {
          console.error("Failed to fetch guest details:", error);
          setGuestData(null);
        } finally {
          setLoading(false);
        }
      };
      fetchGuestDetails();
    }
  }, [show, guestId, api]);

  useEffect(() => {
    const fetchHistory = async () => {
      if (
        !show ||
        !guestId ||
        activeTab === "overview" ||
        activeTab === "notes" ||
        activeTab === "activities"
      ) {
        return;
      }
      setHistoryLoading(true);
      try {
        const params = new URLSearchParams({
          page: currentPage,
          limit: rowsPerPage,
          type: activeTab,
        }).toString();

        const { data } = await api.get(
          `/customer/${guestId}/details?${params}`,
        );
        setHistory(data.history.docs || []);
        setTotalRows(data.history.totalDocs || 0);
      } catch (error) {
        console.error(`Failed to fetch ${activeTab} history:`, error);
        setHistory([]);
        setTotalRows(0);
      } finally {
        setHistoryLoading(false);
      }
    };

    fetchHistory();
  }, [show, guestId, api, activeTab, currentPage, rowsPerPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = async (newRowsPerPage, page) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(page);
  };

  const historyColumns = [
    {
      name: "Date",
      selector: (row) => moment(row.order_date).format(DISPLAY_DATE_FORMAT),
      sortable: true,
    },
    {
      name: "Time",
      selector: (row) =>
        moment(row.order_time, DB_HOURS_FORMAT).format("h:mm A"),
      sortable: true,
    },
    {
      name: "Store",
      selector: (row) => row.store?.name || "N/A",
      sortable: true,
    },
    {
      name: "Status",
      cell: (row) => (
        <Badge
          variant={row.status.includes("cancelled") ? "danger" : "primary"}
        >
          {row.status}
        </Badge>
      ),
      sortable: true,
    },
    {
      name: "Total",
      selector: (row) => toPesoAmount(row.originalTotalPrice || 0),
      sortable: true,
      right: true,
    },
  ];

  const renderHistoryTable = () => {
    return (
      <DataTable
        columns={historyColumns}
        data={history}
        progressPending={historyLoading}
        progressComponent={<PacmanWrapper />}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        onChangeRowsPerPage={handleRowsPerPageChange}
        onChangePage={handlePageChange}
        striped
        highlightOnHover
        responsive
      />
    );
  };

  const renderActiveReservations = (reservations) => {
    if (!reservations || reservations.length === 0) {
      return <p className="text-muted text-center">No active reservations.</p>;
    }

    const handleReservationClick = (reservation) => {
      if (onReservationClick) onReservationClick(reservation);
      onHide();
    };

    return (
      <Table striped bordered hover size="sm" responsive>
        <thead>
          <tr>
            <th>Date</th>
            <th>Time</th>
            <th>Store</th>
            <th>Details</th>
            <th>Pax</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {reservations.map((res) => {
            const experience = res.order_details.find(
              (d) => d.item.type === "reservation_experience",
            );
            const table = res.order_details.find(
              (d) => d.item.type === "reservation",
            );
            const guestCount = get(experience, "qty", 0);

            return (
              <tr
                key={res._id}
                onClick={() => handleReservationClick(res)}
                style={{ cursor: "pointer" }}
              >
                <td>{moment(res.order_date).format("MMM D, YYYY")}</td>
                <td>{moment(res.order_time, "HH:mm").format("h:mm A")}</td>
                <td>{res.store.name}</td>
                <td>
                  {experience?.item.name} @ {table?.item.name}
                </td>
                <td>{guestCount}</td>
                <td>
                  <span
                    className="font-weight-bold text-info"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevents the row's onClick from firing too
                      handleReservationClick(res);
                    }}
                  >
                    VIEW
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  };

  const renderNotes = (notes) => {
    if (!notes || notes.length === 0)
      return <p>No notes found for this guest.</p>;
    return (
      <ul className="list-group">
        {notes.map((note, index) => (
          <li key={note._id || index} className="list-group-item">
            <p className="mb-1">{note.notes || note.note}</p>
            <small className="text-muted">
              {moment(note.date_created).format("MMM D, YYYY, h:mm A")}
            </small>
          </li>
        ))}
      </ul>
    );
  };

  const renderActivities = (activities) => {
    if (!activities || activities.length === 0)
      return <p>No recent activity.</p>;
    return (
      <ul className="list-group">
        {activities.map((activity) => (
          <li key={activity._id} className="list-group-item">
            <p className="mb-1">{activity.details}</p>
            <small className="text-muted">
              {moment(activity.date_created).format("MMM D, YYYY, h:mm A")}
            </small>
          </li>
        ))}
      </ul>
    );
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header className="bg-light">
        <Modal.Title className="d-flex flex-column">
          <div className="d-flex align-items-center">
            <h4 className="mb-0">
              {loading
                ? "Loading Guest..."
                : `${guestData?.customer?.first_name} ${guestData?.customer?.last_name}`}
            </h4>
            {guestData?.customer?.level === "VIP" && (
              <Badge variant="warning" className="ml-2">
                VIP
              </Badge>
            )}
          </div>
          {!loading && guestData && (
            <small className="text-muted">
              {guestData.customer.email} • {guestData.customer.phone}
            </small>
          )}
        </Modal.Title>
        <button
          type="button"
          className="close"
          data-dismiss="modal"
          aria-label="Close"
          onClick={onHide}
        >
          <i aria-hidden="true" className="ki ki-close"></i>
        </button>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="d-flex flex-column align-items-center py-5">
            <PacmanWrapper />
            <small className="mt-2 text-muted">Fetching guest details…</small>
          </div>
        ) : !guestData ? (
          <p>Could not load guest information.</p>
        ) : (
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            id="guest-info-tabs"
            className="mb-3"
          >
            {/* ========== OVERVIEW TAB ========== */}
            <Tab eventKey="overview" title="📋 Overview">
              <div className="row">
                {/* PROFILE CARD */}
                <div className="col-md-4">
                  <div className="card shadow-sm p-3 h-100 text-center">
                    {/* --- Initials Avatar --- */}
                    <div
                      className="mx-auto mb-3 d-flex align-items-center justify-content-center"
                      style={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        backgroundColor: "#6c757d",
                        color: "white",
                        fontSize: "2rem",
                        fontWeight: "700",
                      }}
                    >
                      {getInitials(
                        guestData.customer.first_name,
                        guestData.customer.last_name,
                      )}
                    </div>

                    {/* --- Name & VIP --- */}
                    <h5 className="mb-1">
                      {guestData.customer.first_name}{" "}
                      {guestData.customer.last_name}
                      {guestData.customer.level === "VIP" && (
                        <Badge variant="warning" className="ml-2">
                          VIP
                        </Badge>
                      )}
                    </h5>

                    {/* --- Contact --- */}
                    <p className="text-muted mb-2">
                      {guestData.customer.email} • {guestData.customer.phone}
                    </p>

                    {/* --- Tags --- */}
                    <p>
                      {(guestData.customer.tags || []).map((tag) => (
                        <Badge key={tag} variant="info" className="mr-1 mb-1">
                          {tag}
                        </Badge>
                      ))}
                    </p>

                    {/* --- Latest Note --- */}
                    {guestData.latest_note && (
                      <div className="mt-3 p-2 bg-light rounded">
                        <strong>Latest Note</strong>
                        <p className="mb-1">
                          “
                          {guestData.latest_note.notes ||
                            guestData.latest_note.note}
                          ”
                        </p>
                        <small className="text-muted">
                          {moment(guestData.latest_note.date_created).format(
                            "MMM D, YYYY",
                          )}
                        </small>
                      </div>
                    )}

                    {/* --- Most Ordered --- */}
                    {guestData.most_ordered_item && (
                      <p className="mt-3">
                        <strong>Most Ordered:</strong>
                        <br />
                        {guestData.most_ordered_item.name}
                        <br />
                        <small className="text-muted">
                          {guestData.most_ordered_item.count} orders
                        </small>
                      </p>
                    )}
                  </div>
                </div>

                {/* STATS CARD */}
                <div className="col-md-8">
                  <div className="card shadow-sm p-3 h-100">
                    <h5>Guest Activity</h5>
                    <hr />

                    {/* TOTAL VISITS */}
                    <div className="text-center mb-4">
                      <div className="text-muted">TOTAL VISITS</div>
                      <div style={{ fontSize: "2.5rem", fontWeight: "700" }}>
                        {guestData.stats.visits}
                      </div>
                    </div>

                    {/* STAT GRID */}
                    <div className="row text-center">
                      {[
                        ["Reservations", guestData.stats.reservations],
                        ["Events", guestData.stats.events],
                        ["Orders", guestData.stats.orders],
                        ["Walk-ins", guestData.stats.walk_ins],
                        ["No Shows", guestData.stats.no_shows],
                        ["Cancelled", guestData.stats.cancellations],
                      ].map(([label, value]) => (
                        <div key={label} className="col-4 mb-3">
                          <div className="border rounded p-2 h-100">
                            <small className="text-muted">{label}</small>
                            <div className="h5 mb-0">{value || 0}</div>
                          </div>
                        </div>
                      ))}

                      {/* REVENUE */}
                      <div className="col-12">
                        <div className="border rounded p-3 bg-light">
                          <small className="text-muted">Revenue</small>
                          <div className="h4 mb-0">
                            {toPesoAmount(guestData.stats.revenue || 0)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ACTIVE RESERVATIONS */}
                    <div className="mt-4">
                      <h5 className="mb-3">Active Reservations</h5>
                      {renderActiveReservations(guestData.active_reservations)}
                    </div>
                  </div>
                </div>
              </div>
            </Tab>

            {/* ========== HISTORY TABS ========== */}
            <Tab
              eventKey="reservations"
              title={`📅 Reservations (${guestData.stats.reservations || 0})`}
            >
              {renderHistoryTable()}
            </Tab>

            <Tab
              eventKey="events"
              title={`🎉 Events (${guestData.stats.events || 0})`}
            >
              {renderHistoryTable()}
            </Tab>

            <Tab
              eventKey="orders"
              title={`🛒 Orders (${guestData.stats.orders || 0})`}
            >
              {renderHistoryTable()}
            </Tab>

            <Tab eventKey="notes" title="📝 Notes">
              {renderNotes(guestData.notes)}
            </Tab>

            <Tab eventKey="activities" title="⚡ Activities">
              {renderActivities(guestData.activities)}
            </Tab>
          </Tabs>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default GuestInformationModal;
